import { BookingDetails } from '@jurnee/common/src/entities/Booking';
import { User, UserDetails, UserJSON, UsersSearchParams, UsersSearchQuery, UserUpdate } from '@jurnee/common/src/entities/User';
import { sortAlphabeticallyBy } from './arrays';
import { MAX_LIMIT } from './pagination';
import { isEmpty } from './strings';

function parseIds(value: NonNullable<UsersSearchQuery['userIds']>): number[] | null {
  // Express's extended parser represents long arrays as numeric-keyed objects.
  const values = typeof value === 'string' ? [value] : Object.values(value);

  if (values.length > MAX_LIMIT || values.some(entry => typeof entry !== 'string')) {
    return null;
  }

  return values.map(Number).filter(id => Number.isSafeInteger(id) && id > 0);
}

export function buildUsersSearchParams(query: UsersSearchQuery): UsersSearchParams | null {
  const params: UsersSearchParams = {};

  for (const key of ['budgetIds', 'teamIds', 'userIds'] as const) {
    if (query[key] === undefined) {
      continue;
    }

    const ids = parseIds(query[key]);

    if (ids === null) {
      return null;
    }

    params[key] = ids;
  }

  if (query.search && !isEmpty(query.search)) {
    params.search = query.search.trim();
  }

  return params;
}

export function trimFullName(user: Pick<User, 'firstName' | 'lastName'>) {
  const names = [];

  if (user.firstName && !isEmpty(user.firstName)) {
    names.push(user.firstName.trim());
  }

  if (user.lastName && !isEmpty(user.lastName)) {
    names.push(user.lastName.trim());
  }

  return names.join(' ');
}

export function getUserLabel(user: Pick<User, 'firstName' | 'lastName' | 'email'>) {
  if (!user) {
    return 'Unknown';
  }

  if (user.firstName) {
    return trimFullName(user);
  }

  return user.email;
}

export function sortByUserLabel<T extends Pick<UserJSON, 'firstName' | 'lastName' | 'email'>>(users: T[]) {
  return sortAlphabeticallyBy(users.map(user => ({ ...user, label: getUserLabel(user) })), 'label');
}

export function isAdmin(user: UserDetails) {
  return Array.isArray(user.usersRoles) && user.usersRoles.some(({ role }) => role.key === 'ADMIN');
}

export function isEventManager(user: UserDetails) {
  return Array.isArray(user.usersRoles) && user.usersRoles.some(({ role }) => role.key === 'EVENT_MANAGER');
}

export function isOrganizer(user: UserDetails) {
  return Array.isArray(user.usersRoles) && user.usersRoles.some(({ role }) => role.key === 'ORGANIZER');
}

export function getUserRole(user: UserDetails) {
  if (isAdmin(user)) {
    const userRole = user.usersRoles.find(({ role }) => role.key === 'ADMIN');
    return userRole?.role;
  }

  if (isEventManager(user)) {
    const userRole = user.usersRoles.find(({ role }) => role.key === 'EVENT_MANAGER');
    return userRole?.role;
  }

  if (isOrganizer(user)) {
    const userRole = user.usersRoles.find(({ role }) => role.key === 'ORGANIZER');
    return userRole?.role;
  }

  return null;
}

export function getDomainFromEmail(email: string) {
  return email.split('@').pop();
}

export function getInviteLink(baseUrl: string, token: string, email: UserDetails['email'], bookingId?: BookingDetails['id']) {
  const link = new URL(`${baseUrl}/signup`);

  link.searchParams.set('email', email);

  if (bookingId) {
    link.searchParams.set('redirect', `/bookings/${bookingId}`);
  }

  link.searchParams.set('token', token);

  return link.toString();
}

export function cleanData<T extends UserUpdate>(data: T): T {
  if (typeof data.email === 'string') {
    data.email = data.email.trim().toLowerCase();
  }

  if (typeof data.firstName === 'string') {
    data.firstName = data.firstName.trim();
  }

  if (typeof data.lastName === 'string') {
    data.lastName = data.lastName.trim();
  }

  return data;
}